import { useState } from 'react';
import { api, ApiError } from '../../api';
import type { Tenancy, UpdateTenancyPayload } from '../../api';
import { useToast } from '../../components/Toast';
import {
  Sheet,
  Field,
  FieldRow,
  TextInput,
  MoneyInput,
  Segmented,
  ToggleRow,
  SectionLabel,
  ErrorBanner,
  PrimaryBtn,
  GhostBtn,
  rupeesToPaise,
  paiseToRupees,
  dateInputToEpoch,
  epochToDateInput,
  pctToBps,
} from './Sheet';

const GST_OPTS = [
  { value: 'none', label: 'None' },
  { value: 'add', label: 'Add GST' },
  { value: 'rcm', label: 'RCM' },
];

/** Patch a tenancy's terms + all toggles (PATCH /tenancies/:id). */
export function EditTenancy({
  tenancy,
  tenantName,
  onClose,
  onSaved,
}: {
  tenancy: Tenancy;
  tenantName?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useToast();

  // camAmount is on the server row but not in the trimmed Tenancy view-type.
  const camAmt = (tenancy as { camAmount?: number | null }).camAmount ?? null;

  const [rent, setRent] = useState(paiseToRupees(tenancy.rentAmount));
  const [deposit, setDeposit] = useState(paiseToRupees(tenancy.depositAmount));
  const [dueDay, setDueDay] = useState(String(tenancy.rentDueDay ?? 5));
  const [startDate, setStartDate] = useState(epochToDateInput(tenancy.startDate));
  const [endDate, setEndDate] = useState(epochToDateInput(tenancy.endDate));

  const [tdsEnabled, setTdsEnabled] = useState(!!tenancy.tdsEnabled);
  const [gstMode, setGstMode] = useState<'none' | 'add' | 'rcm'>(tenancy.gstMode ?? 'none');
  const [escEnabled, setEscEnabled] = useState(!!tenancy.escalationEnabled);
  const [escPct, setEscPct] = useState(tenancy.escalationPctBps ? String(tenancy.escalationPctBps / 100) : '5');
  const [escCycle, setEscCycle] = useState(String(tenancy.escalationCycleMonths ?? 12));
  const [camEnabled, setCamEnabled] = useState(!!tenancy.camEnabled);
  const [camAmount, setCamAmount] = useState(paiseToRupees(camAmt));
  const [elecEnabled, setElecEnabled] = useState(!!tenancy.electricityEnabled);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rentPaise = rupeesToPaise(rent);
  const canSubmit = rentPaise != null && rentPaise > 0;

  async function save() {
    setError(null);
    if (rentPaise == null || rentPaise <= 0) return setError('Enter a valid monthly rent.');
    const depositPaise = rupeesToPaise(deposit) ?? 0;
    const camPaise = camEnabled ? rupeesToPaise(camAmount) : null;
    if (camEnabled && (camPaise == null || camPaise < 0)) return setError('Enter a valid CAM amount.');

    setBusy(true);
    try {
      const patch: UpdateTenancyPayload = {
        rentAmount: rentPaise,
        depositAmount: depositPaise,
        rentDueDay: Number(dueDay) || 5,
        startDate: dateInputToEpoch(startDate),
        endDate: dateInputToEpoch(endDate),
        tdsEnabled,
        gstMode,
        escalationEnabled: escEnabled,
        escalationPctBps: escEnabled ? pctToBps(escPct) : null,
        escalationCycleMonths: escEnabled ? Number(escCycle) || 12 : null,
        camEnabled,
        camAmount: camEnabled ? camPaise : null,
        electricityEnabled: elecEnabled,
      };
      await api.updateTenancy(tenancy.id, patch);
      notify('Tenancy updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the tenancy.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      icon="key"
      title="Edit tenancy"
      subtitle={tenantName}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn>
          <PrimaryBtn onClick={save} disabled={!canSubmit} busy={busy}>Save changes</PrimaryBtn>
        </>
      }
    >
      <ErrorBanner message={error} />

      <SectionLabel>Lease terms</SectionLabel>
      <FieldRow>
        <Field label="Monthly rent" required>
          <MoneyInput value={rent} onChange={setRent} placeholder="90000" />
        </Field>
        <Field label="Deposit">
          <MoneyInput value={deposit} onChange={setDeposit} placeholder="0" />
        </Field>
      </FieldRow>
      <FieldRow>
        <Field label="Rent due day" hint="Day of month (1–28).">
          <TextInput value={dueDay} onChange={(v) => setDueDay(v.replace(/[^0-9]/g, ''))} inputMode="numeric" />
        </Field>
        <Field label="Start date">
          <TextInput value={startDate} onChange={setStartDate} type="date" />
        </Field>
      </FieldRow>
      <Field label="End date" hint="Leave blank for an open-ended tenancy.">
        <TextInput value={endDate} onChange={setEndDate} type="date" />
      </Field>

      <SectionLabel>Adjustments</SectionLabel>
      <ToggleRow
        label="Tenant deducts TDS"
        hint="Net expected drops by the standard 10% the tenant withholds."
        on={tdsEnabled}
        onToggle={setTdsEnabled}
      />
      <Field label="GST treatment">
        <Segmented value={gstMode} onChange={(v) => setGstMode(v as 'none' | 'add' | 'rcm')} options={GST_OPTS} />
      </Field>
      <ToggleRow label="Annual escalation" hint="Bumps base rent on a fixed cycle." on={escEnabled} onToggle={setEscEnabled}>
        <FieldRow>
          <Field label="Escalation %">
            <TextInput value={escPct} onChange={(v) => setEscPct(v.replace(/[^0-9.]/g, ''))} inputMode="decimal" />
          </Field>
          <Field label="Every N months">
            <TextInput value={escCycle} onChange={(v) => setEscCycle(v.replace(/[^0-9]/g, ''))} inputMode="numeric" />
          </Field>
        </FieldRow>
      </ToggleRow>
      <ToggleRow label="CAM charge" hint="Common-area maintenance added each month." on={camEnabled} onToggle={setCamEnabled}>
        <Field label="CAM amount / month">
          <MoneyInput value={camAmount} onChange={setCamAmount} placeholder="0" />
        </Field>
      </ToggleRow>
      <ToggleRow
        label="Electricity billed separately"
        hint="Track a metered electricity line on the ledger."
        on={elecEnabled}
        onToggle={setElecEnabled}
      />
    </Sheet>
  );
}
